import { MIN_CHARGE as COUPON_MIN_CHARGE } from './couponPricing';

const round2 = (n: number) => Math.round(n * 100) / 100

// Espelho no navegador de supabase/functions/_shared/plan.ts (mantenha os dois iguais). Serve para a tela
// mostrar os valores; quem cobra de verdade é sempre o servidor.
//
// O plano é sempre de 12 mensalidades, em qualquer forma de pagamento. Este arquivo decide o que se cobra
// no cadastro e quando começa a assinatura.

export type BillingMode = 'pix' | 'card_auto' | 'card_12x'

export const CONTRACT_MONTHS = 12
export const INSTALLMENTS = 12
export const INSTALLMENT_DISCOUNT = 0.05
/** O Asaas não cobra abaixo disso (por cobrança ou parcela). */
export const MIN_CHARGE = COUPON_MIN_CHARGE

export interface PlanInput {
  mode: BillingMode
  includeImplementation: boolean
  /** Implantação já com o desconto do cupom (ignorada se não incluída). */
  implementation: number
  /** Mensalidade já com o desconto do cupom. */
  monthly: number
  freeMonths: number
}

export interface Plan {
  mode: BillingMode
  includeImplementation: boolean
  implementation: number
  monthly: number
  freeMonths: number
  /** Valor cobrado no cadastro (no 12x, o total parcelado). */
  chargeNow: number
  /** 12x: soma antes do desconto de 5%. Demais: igual a chargeNow. */
  subtotal: number
  installmentDiscount: number
  installmentCount: number | null
  installmentValue: number | null
  /** A 1ª mensalidade é o próprio pagamento do cadastro (sem implantação e sem mês grátis). */
  paysMonthlyAtSignup: boolean
  /** Meses entre o pagamento e a 1ª cobrança da assinatura (no 12x, da renovação mensal). */
  subscriptionOffsetMonths: number
  /** Meses entre o pagamento e o fim das 12 mensalidades. */
  contractEndOffsetMonths: number
}

export function computePlan(i: PlanInput): Plan {
  const implementation = i.includeImplementation ? round2(i.implementation) : 0
  const base = {
    mode: i.mode,
    includeImplementation: i.includeImplementation,
    implementation,
    monthly: i.monthly,
    freeMonths: i.freeMonths,
  }

  if (i.mode === 'card_12x') {
    // 12 mensalidades pagas à vista no cartão, parceladas; os meses grátis do cupom estendem o período
    const subtotal = round2(i.monthly * CONTRACT_MONTHS + implementation)
    const total = round2(subtotal * (1 - INSTALLMENT_DISCOUNT))
    const offset = (i.includeImplementation ? 1 : 0) + i.freeMonths + CONTRACT_MONTHS
    return {
      ...base,
      chargeNow: total,
      subtotal,
      installmentDiscount: round2(subtotal - total),
      installmentCount: INSTALLMENTS,
      installmentValue: round2(total / INSTALLMENTS),
      paysMonthlyAtSignup: false,
      subscriptionOffsetMonths: offset,
      contractEndOffsetMonths: offset,
    }
  }

  // Recorrente (cartão automático ou Pix mensal)
  const paysMonthlyAtSignup = !i.includeImplementation && i.freeMonths === 0
  const chargeNow = i.includeImplementation ? implementation : paysMonthlyAtSignup ? i.monthly : 0
  const subscriptionOffsetMonths = i.includeImplementation ? 1 + i.freeMonths : Math.max(i.freeMonths, 1)
  const remaining = paysMonthlyAtSignup ? CONTRACT_MONTHS - 1 : CONTRACT_MONTHS
  return {
    ...base,
    chargeNow,
    subtotal: chargeNow,
    installmentDiscount: 0,
    installmentCount: null,
    installmentValue: null,
    paysMonthlyAtSignup,
    subscriptionOffsetMonths,
    contractEndOffsetMonths: subscriptionOffsetMonths + remaining,
  }
}

/** Mensagem se o plano resulta em algo que o Asaas não consegue cobrar; null se ok. */
export function planError(p: Plan): string | null {
  if (p.mode === 'card_12x') {
    if ((p.installmentValue ?? 0) < MIN_CHARGE) return 'Parcelamento indisponível para este valor.'
    return null
  }
  if (p.chargeNow > 0 && p.chargeNow < MIN_CHARGE) return 'Valor abaixo do mínimo cobrável.'
  return null
}
